import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GATES,
  SEQUENTIAL,
  BLOCKS,
  LABELS,
  CLOCK_MODES,
  blankProject,
  component,
  uid,
  pins,
  sourceKey,
  isSequential,
} from "../engine/registry";
import { generate, defaultGenerator, autoLayout } from "../engine/generator";
import { validate } from "../engine/validation";
import { simulate } from "../engine/simulator";
import { prepareTiming, givenResult } from "../engine/timingPresentation";
import {
  clone,
  deserialize,
  downloadProject,
  serialize,
} from "../utils/projects";
import {
  connect,
  removeSelection,
  copySelection,
  pasteSelection,
} from "../utils/editing";
import {
  attachMissingTerminals,
  normalizeClocks,
} from "../utils/circuitEditing";
import { exportDocument } from "../utils/exports";
import CircuitCanvas from "./CircuitCanvas";
import GeneratorWizard from "./GeneratorWizard";
import TimingStudio from "./TimingStudio";
import ComponentDialog, { Dialog, TerminalDialog } from "./ComponentDialog";
import HeaderActions from "./HeaderActions";
import ExportDialog from "./ExportDialog";
const STORAGE = "tdg-v3-circuit";
export function upgradeDiagram(project) {
  const p = clone(project);
  if (p.metadata.uiRevision === 31) return p;
  p.components.filter(isSequential).forEach((c, i) => {
    c.config.output =
      String.fromCharCode(65 + (i % 26)) + (i >= 26 ? Math.floor(i / 26) : "");
    c.config.pinLabels = { ...c.config.pinLabels };
    pins(c).inputs.forEach((pin) => {
      if (!c.config.pinLabels[pin.id])
        c.config.pinLabels[pin.id] = pin.control
          ? "clk"
          : pin.id + c.config.output.toLowerCase();
    });
  });
  // Old shared clocks may have different receiving polarities; preserve each behavior.
  for (const clock of [...p.components.filter((c) => c.type === "CLOCK")]) {
    if (clock.config.activation) continue;
    const groups = new Map();
    p.connections
      .filter(
        (e) =>
          e.from.component === clock.id && ["CLK", "EN"].includes(e.to.pin),
      )
      .forEach((e) => {
        const c = p.components.find((c) => c.id === e.to.component);
        if (c && isSequential(c)) {
          const mode = `${c.config.trigger}_${c.type === "SR_LATCH" ? "LEVEL" : "EDGE"}`;
          groups.set(mode, [...(groups.get(mode) || []), e]);
        }
      });
    if (!groups.size) clock.config.activation = "POS_EDGE";
    let first = true;
    for (const [mode, wires] of groups) {
      let current = clock;
      if (!first) {
        current = {
          ...clone(clock),
          id: uid("clock"),
          name: `${clock.name}_${mode.toLowerCase()}`,
        };
        p.components.push(current);
      }
      current.config = { ...current.config, activation: mode };
      wires.forEach((e) => (e.from = { component: current.id, pin: "CLK" }));
      first = false;
    }
  }
  p.metadata.uiRevision = 31;
  return autoLayout(p);
}
function load() {
  try {
    const data = localStorage.getItem(STORAGE);
    if (data) return upgradeDiagram(deserialize(data));
  } catch {}
  return upgradeDiagram(generate(defaultGenerator()));
}
export default function CircuitStudio({ mode, onModeChange, notify }) {
  const [project, setProject] = useState(load),
    [undo, setUndo] = useState([]),
    [redo, setRedo] = useState([]),
    [selected, setSelected] = useState([]),
    [selectedWire, setSelectedWire] = useState(null),
    [wizard, setWizard] = useState(false),
    [namesOpen, setNamesOpen] = useState(false),
    [editId, setEditId] = useState(null),
    [checksOpen, setChecksOpen] = useState(false),
    [fitToken, setFitToken] = useState(0),
    [settings, setSettings] = useState(
      () => project.metadata.generator || defaultGenerator(),
    ),
    [search, setSearch] = useState(""),
    [exportOpen, setExportOpen] = useState(false),
    [srId, setSrId] = useState(null),
    [terminalEdit, setTerminalEdit] = useState(null),
    [terminalError, setTerminalError] = useState(""),
    [fullscreen, setFullscreen] = useState(false),
    [armed, setArmed] = useState(null),
    [session, setSession] = useState(0);
  const clipboard = useRef(null),
    fileInput = useRef(null),
    frame = useRef(null),
    terminalTray = useRef(null),
    terminalField = useRef(null);
  const checked = useMemo(() => validate(project), [project]);
  useEffect(()=>{if(terminalEdit?.id){terminalField.current?.focus();terminalField.current?.select();terminalField.current?.scrollIntoView?.({block:'nearest',behavior:'smooth'});}},[terminalEdit?.id]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE, serialize(project));
    } catch {}
  }, [project]);
  useEffect(() => {
    const listener = () => {
      if (!document.fullscreenElement) setFullscreen(false);
      setFitToken((n) => n + 1);
    };
    document.addEventListener("fullscreenchange", listener);
    return () => document.removeEventListener("fullscreenchange", listener);
  }, []);
  useEffect(() => {
    if (fullscreen) {
      const before = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = before;
      };
    }
  }, [fullscreen]);
  const commit = (next) => {
    if (JSON.stringify(next) === JSON.stringify(project)) return;
    setUndo((h) => [...h.slice(-79), project]);
    setRedo([]);
    setProject(next);
  };
  const goUndo = () => {
    if (!undo.length) return;
    setRedo((h) => [project, ...h]);
    setProject(undo.at(-1));
    setUndo((h) => h.slice(0, -1));
    setSelected([]);
    setSelectedWire(null);
  };
  const goRedo = () => {
    if (!redo.length) return;
    setUndo((h) => [...h, project]);
    setProject(redo[0]);
    setRedo((h) => h.slice(1));
    setSelected([]);
    setSelectedWire(null);
  };
  const remove = () => {
    commit(removeSelection(project, selected, selectedWire));
    setSelected([]);
    setSelectedWire(null);
    setEditId(null);
  };
  const copy = () => {
    if (!selected.length) return;
    clipboard.current = copySelection(project, selected);
    notify(`${selected.length} component(s) copied.`);
  };
  const paste = (duplicate) => {
    const data = duplicate
      ? copySelection(project, selected)
      : clipboard.current;
    if (!data?.components.length) return;
    if (project.components.length + data.components.length > 200) {
      notify("Maximum 200 components.", true);
      return;
    }
    const next = pasteSelection(project, data);
    commit(next.project);
    setSelected(next.ids);
    setSelectedWire(null);
  };
  const add = (token, position) => {
    if (project.components.length >= 200) {
      notify("Maximum 200 components.", true);
      return;
    }
    const clockMode = token.startsWith("CLOCK_") ? token.slice(6) : null,
      type = clockMode ? "CLOCK" : token,
      p = clone(project),
      count = p.components.filter((c) => c.type === type).length + 1;
    const c = component(
      type,
      count,
      position?.x ?? 80 + (p.components.length % 4) * 210,
      position?.y ?? 70 + Math.floor(p.components.length / 4) * 155,
      clockMode ? { activation: clockMode } : {},
    );
    const takenNames = new Set([...p.components,...p.inputs].map(x=>x.name));
    let name = c.name,
      n = 2;
    while (takenNames.has(name))
      name = `${c.name}_${n++}`;
    c.name = name;
    if (isSequential(c)) {
      const used = new Set(
        p.components.filter(isSequential).map((c) => c.config.output),
      );
      let i = 0;
      while (used.has(String.fromCharCode(65 + i))) i++;
      c.config.output = String.fromCharCode(65 + i);
    }
    p.components.push(c);
    pins(c)
      .outputs
      .forEach((pin) => {
        const source = { component: c.id, pin: pin.id };
        p.observables.push({ id: sourceKey(source), source });
      });
    p.metadata.uiRevision = 31;
    commit(p);
    setSelected([c.id]);
    setSelectedWire(null);
    if(c.type.startsWith("SR"))setSrId(c.id);
  };
  const wire = (from, to) => {
    try {
      commit(normalizeClocks(connect(project, from, to)));
      setSelectedWire(null);
    } catch (e) {
      notify(e.message, true);
    }
  };
  const addInput = () => {
    const p = clone(project),
      taken = new Set([
        ...p.inputs.map((i) => i.name),
        ...p.components.map((c) => c.name),
      ]);
    let n = 1;
    while (taken.has(`P${n}`)) n++;
    const input = {
      id: uid("input"),
      name: `P${n}`,
      values: Array.from({ length: 16 }, () => (Math.random() < 0.5 ? 0 : 1)),
    };
    p.inputs.push(input);
    commit(p);
    setArmed({ input: input.id });
    notify(`Click a component input pin to connect ${input.name}.`);
  };
  const generateNow = (cfg) => {
    const next = upgradeDiagram(generate(cfg));
    simulate(next);
    commit(next);
    setSettings(cfg);
    setWizard(false);
    setNamesOpen(true);
    setSelected([]);
    setSelectedWire(null);
    setFitToken((n) => n + 1);
  };
  const full = async () => {
    if (fullscreen) {
      if (document.fullscreenElement) await document.exitFullscreen?.();
      setFullscreen(false);
    } else {
      setFullscreen(true);
      try {
        await frame.current?.requestFullscreen?.();
      } catch {
        /* The fixed canvas remains usable when native fullscreen is denied. */
      }
    }
    requestAnimationFrame(() => setFitToken((n) => n + 1));
  };
  const confirm = async () => {
    try {
      const connected = attachMissingTerminals(project),
        check = validate(connected);
      if (!check.valid) {
        commit(connected);
        setChecksOpen(true);
        return;
      }
      const ready = prepareTiming(
        connected,
        project.timing.cycles || 8,
        `${project.metadata.seed}:${Date.now()}`,
      );
      simulate(ready);
      ready.timing.requestInitialStates = ready.components.some(isSequential);
      commit(ready);
      setSession((s) => s + 1);
      if (document.fullscreenElement) await document.exitFullscreen?.();
      setFullscreen(false);
      onModeChange("SIMULATION");
    } catch (e) {
      notify(e.message, true);
    }
  };
  const exportFile = async options => {
    if(options.scope !== 'circuit' && !checked.valid) throw new Error('Resolve circuit validation errors before exporting its timing diagram.');
    const ready=options.scope==='circuit'?project:prepareTiming(project,project.timing.cycles||8);
    const output=await exportDocument(ready,options.scope==='circuit'?null:givenResult(ready),{...options,question:true});
    notify(`Export ready · ${output.pages} A4 page(s).`);
  };
  const focusTerminal = id => {
    const input=project.inputs.find(i=>i.id===id);if(!input)return;
    if(terminalTray.current)terminalTray.current.open=true;
    setTerminalEdit({id,name:input.name});setTerminalError('');
  };
  const saveTerminal = () => {
    if(!terminalEdit)return;
    const name=terminalEdit.name.trim();
    if(!name||project.inputs.some(i=>i.id!==terminalEdit.id&&i.name===name)||project.components.some(c=>c.name===name)){setTerminalError('Use a nonempty, unique variable name.');return;}
    const p=clone(project);p.inputs.find(i=>i.id===terminalEdit.id).name=name;commit(p);setTerminalEdit(null);setTerminalError('');
  };
  const selectedNode = id => {const c=project.components.find(c=>c.id===id);if(c?.type.startsWith('SR'))setSrId(id);};
  if (mode === "SIMULATION")
    return (
      <TimingStudio
        key={session}
        project={project}
        onChange={commit}
        onBack={() => onModeChange("CIRCUIT")}
        notify={notify}
      />
    );
  return (
    <div
      ref={frame}
      className={`editor-frame ${fullscreen ? "is-fullscreen" : ""}`}
    >
      <HeaderActions><div className="button-row"><button className="primary" disabled={!project.components.length} onClick={()=>setExportOpen('standard')}>Export…</button><button disabled={!project.components.length} onClick={()=>setExportOpen('question')}>Export as question</button></div></HeaderActions>
      <input
        ref={fileInput}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          try {
            const p = upgradeDiagram(deserialize(await file.text()));
            commit(p);
            setSelected([]);
            setSelectedWire(null);
            setFitToken((n) => n + 1);
            if (p.metadata.generator) setSettings(p.metadata.generator);
            notify("Editable project loaded.");
          } catch (error) {
            notify(error.message, true);
          }
          e.target.value = "";
        }}
      />
      <div className="workspace-toolbar compact-toolbar">
        <div className="button-row">
          <button
            onClick={() => {
              const p = blankProject();
              p.metadata.uiRevision = 31;
              commit(p);
              setSelected([]);
              setSelectedWire(null);
              setFitToken((n) => n + 1);
            }}
          >
            New
          </button>
          <button onClick={() => fileInput.current.click()}>Open</button>
          <button onClick={() => downloadProject(project)}>Save project</button>
          <span className="toolbar-divider" />
          <button
            title="Undo · Ctrl+Z"
            disabled={!undo.length}
            onClick={goUndo}
          >
            ↶
          </button>
          <button
            title="Redo · Ctrl+Y"
            disabled={!redo.length}
            onClick={goRedo}
          >
            ↷
          </button>
          <button className="primary" onClick={() => setWizard(true)}>
            Generate
          </button>
          <button
            onClick={() => {
              commit(autoLayout(project));
              setFitToken((n) => n + 1);
            }}
          >
            Arrange for A4
          </button>
          <button onClick={() => setNamesOpen(true)}>Terminal names</button>
        </div>
        <div className="button-row">
          <button onClick={() => setChecksOpen(true)}>
            Validate{checked.valid ? " ✓" : ""}
          </button>
          <button className="primary" onClick={confirm}>
            Generate timing diagram →
          </button>
        </div>
      </div>
      <div className="circuit-layout">
        <aside className="palette panel">
          <div className="panel-content">
            <h3>Components</h3>
            <input
              className="search-input"
              aria-label="Search components"
              placeholder="Find a component…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {[
              ["Logic gates", GATES],
              ["Flip-flops & latch", SEQUENTIAL],
              ["Clocks", Object.keys(CLOCK_MODES).map((m) => `CLOCK_${m}`)],
              ["Constants", ["CONST0", "CONST1"]],
              ["Functional blocks", BLOCKS],
            ].map(([label, types]) => {
              const filtered = types.filter((t) =>
                (LABELS[t] || CLOCK_MODES[t.slice(6)] || t)
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              );
              if (!filtered.length) return null;
              return (
                <details
                  key={label}
                  open={label !== "Functional blocks" || !!search}
                  className="palette-group"
                >
                  <summary>{label}</summary>
                  <div
                    className={`palette-grid ${label === "Clocks" ? "clock-palette" : ""}`}
                  >
                    {filtered.map((t) => (
                      <button
                        key={t}
                        draggable
                        className="palette-item"
                        title={LABELS[t] || CLOCK_MODES[t.slice(6)]}
                        onDragStart={(e) =>
                          e.dataTransfer.setData("application/component", t)
                        }
                        onClick={() => add(t)}
                      >
                        {t.startsWith("CLOCK_") ? (
                          CLOCK_MODES[t.slice(6)]
                        ) : t.startsWith("CONST") ? (
                          <span className="constant-glyph">
                            {t === "CONST1" ? "1" : "0"}
                          </span>
                        ) : (
                          t === "COMPARE" ? "Comparator" : t.replace("_FF", " FF").replace("_LATCH", " latch")
                        )}
                      </button>
                    ))}
                  </div>
                </details>
              );
            })}
            <details className="disclosure" open ref={terminalTray}>
              <summary>Input terminals</summary>
              <p className="helper">
                Drag a terminal on the canvas to an input pin, or choose one here and click the destination.
              </p>
              {project.inputs.map(input=><div className={`terminal-row ${terminalEdit?.id===input.id?'is-editing':''}`} key={input.id}>
                {terminalEdit?.id===input.id?<>
                  <input ref={terminalField} className="terminal-name-input" aria-label={`Edit terminal ${input.name}`} value={terminalEdit.name} maxLength="24" onChange={e=>setTerminalEdit({...terminalEdit,name:e.target.value})} onKeyDown={e=>{if(e.key==='Enter')saveTerminal();if(e.key==='Escape'){setTerminalEdit(null);setTerminalError('');}}}/>
                  <button aria-label="Save terminal name" onClick={saveTerminal}>✓</button>
                  {terminalError&&<span className="terminal-inline-message" role="alert">{terminalError}</span>}
                </>:<><button className={`terminal-source ${armed?.input===input.id?'active':''}`} title={`Connect ${input.name}`} onClick={()=>setArmed({input:input.id})}>{input.name} →</button><button aria-label={`Rename ${input.name}`} onClick={()=>focusTerminal(input.id)}>✎</button><button aria-label={`Delete terminal ${input.name}`} onClick={()=>{const p=clone(project);p.inputs=p.inputs.filter(i=>i.id!==input.id);p.connections=p.connections.filter(e=>e.from.input!==input.id);commit(p);}}>×</button></>}
              </div>)}
              <button className="full-width" onClick={addInput}>
                + Add terminal
              </button>
            </details>
          </div>
        </aside>
        <section className="canvas-column">
          <div className="selection-toolbar">
            <span>
              {selected.length
                ? `${selected.length} selected`
                : selectedWire
                  ? "Wire selected"
                  : "Select a component to edit or resize"}
            </span>
            <div className="button-row">
              {selected.length > 0 && (
                <>
                  {selected.length === 1 && (
                    <button onClick={() => setEditId(selected[0])}>
                      Edit component
                    </button>
                  )}
                  <button onClick={copy}>Copy</button>
                  <button onClick={() => paste(true)}>Duplicate</button>
                  <button onClick={remove}>Delete</button>
                </>
              )}
              {selectedWire && (
                <>
                  <span className="helper">Drag a circular handle to bend</span>
                  <button
                    onClick={() => {
                      const p = clone(project),
                        e = p.connections.find((e) => e.id === selectedWire);
                      delete e.waypoints;
                      delete e.anchors;
                      delete e.routeOffset;
                      commit(p);
                    }}
                  >
                    Reset wire
                  </button>
                  <button
                    onClick={() => {
                      const e = project.connections.find(
                        (e) => e.id === selectedWire,
                      );
                      setArmed(e.from);
                    }}
                  >
                    Branch
                  </button>
                  <button onClick={remove}>Delete wire</button>
                </>
              )}
            </div>
          </div>
          <CircuitCanvas
            project={project}
            selected={selected}
            setSelected={setSelected}
            selectedWire={selectedWire}
            setSelectedWire={setSelectedWire}
            onChange={commit}
            onConnect={wire}
            onAdd={add}
            errors={checked.issues
              .filter((i) => i.severity === "error")
              .map((i) => i.component)}
            viewToken={fitToken}
            onDelete={remove}
            onCopy={copy}
            onPaste={paste}
            onUndo={goUndo}
            onRedo={goRedo}
            onEdit={setEditId}
            onEditPin={() => setNamesOpen(true)}
            onTerminalEdit={focusTerminal}
            onNodeClick={selectedNode}
            armedSource={armed}
            onDisarm={() => setArmed(null)}
            onFullscreen={full}
            fullscreen={fullscreen}
          />
          <div className="canvas-bottom">
            <span>
              Coloured wires share a signal. Dots mark branches; crossings are
              independent.
            </span>
            <span>Ctrl+A select all · H toggle pan · Space+drag pan</span>
          </div>
        </section>
      </div>
      {exportOpen && <ExportDialog hasCircuit hasTiming hasAnswer={false} questionMode={exportOpen==='question'} onClose={()=>setExportOpen(false)} onExport={exportFile}/>}
      {srId && project.components.some(c=>c.id===srId) && <Dialog title="SR unused state" onClose={()=>setSrId(null)} footer={<><button onClick={()=>{setEditId(srId);setSrId(null);}}>More settings</button><button className="primary" onClick={()=>setSrId(null)}>Apply</button></>}>
        <p>Choose the output value for the unused S = R = 1 state of {project.components.find(c=>c.id===srId).config.output || project.components.find(c=>c.id===srId).name}.</p>
        <label className="field"><span>When S = R = 1, set Q to</span><select autoFocus aria-label="SR unused-state value" value={project.components.find(c=>c.id===srId).config.policy} onChange={e=>{const p=clone(project);p.components.find(c=>c.id===srId).config.policy=e.target.value;commit(p);}}><option value="X">Unknown (X)</option><option value="0">0</option><option value="1">1</option><option value="HOLD">Hold previous Q</option></select></label>
      </Dialog>}
      {wizard && (
        <GeneratorWizard
          settings={settings}
          onClose={() => setWizard(false)}
          onGenerate={generateNow}
        />
      )}
      {namesOpen && (
        <TerminalDialog
          project={project}
          onChange={commit}
          onClose={() => setNamesOpen(false)}
          onFillMissing={() => commit(attachMissingTerminals(project))}
        />
      )}
      {editId && (
        <ComponentDialog
          project={project}
          id={editId}
          onChange={commit}
          onClose={() => setEditId(null)}
          notify={notify}
        />
      )}
      {checksOpen && (
        <Dialog title="Circuit validation" onClose={() => setChecksOpen(false)}>
          <p>
            {checked.valid
              ? "The circuit is ready to confirm."
              : "Resolve these connections before continuing."}
          </p>
          {checked.issues.map((issue, i) => (
            <button
              className={`validation-row ${issue.severity}`}
              key={i}
              onClick={() => {
                if (issue.component) {
                  setSelected([issue.component]);
                  setEditId(issue.component);
                }
                setChecksOpen(false);
              }}
            >
              <strong>
                {issue.severity === "error" ? "Fix required" : "Note"}
              </strong>
              {issue.message}
            </button>
          ))}
          {!checked.issues.length && (
            <p>All mandatory pins have a valid driver.</p>
          )}
        </Dialog>
      )}
    </div>
  );
}
